import React, { useMemo } from 'react';
import {
    AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, LineChart, Line,
    ScatterChart, Scatter, ZAxis
} from 'recharts';
import { format, parseISO, getDay } from 'date-fns';
import { MockData, MockTransaction } from '../../lib/mockData';
import {
    Activity, TrendingUp, TrendingDown, Layout as LayoutIcon, AlertTriangle,
    CheckCircle2, Clock, ArrowUpRight, ArrowDownRight,
    Target, ShoppingBag, DollarSign
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CHART_TYPES } from '../../lib/api/templates';

interface ChartRendererProps {
    type: string;
    mockData: MockData | null;
}

const CHART_COLORS = {
    revenue: '#10b981',
    expense: '#ef4444',
    net: '#3b82f6',
    accent: '#8b5cf6',
    warning: '#f59e0b',
    info: '#06b6d4',
    pink: '#ec4899',
    grid: '#f1f5f9',
    text: '#94a3b8',
    multi: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#22d3ee']
};

const TOOLTIP_STYLE = {
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    fontSize: '10px'
};

const AD_CATEGORIES = new Set(['Marketing']);
const SHIPPING_CATEGORIES = new Set(['shipping', 'shipping_refund']);
const DISCOUNT_CATEGORIES = new Set(['discount']);
const REFUND_CATEGORIES = new Set(['product_return', 'shipping_refund']);
const FEE_CATEGORIES = new Set(['Software']);

const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
};

// ─── Aggregations ───────────────────────────────────────────────────────────

interface DailyAggregate {
    date: string;
    revenue: number;
    expense: number;
    adSpend: number;
    refunds: number;
    discounts: number;
    shipping: number;
    fees: number;
    orders: number;
    net: number;
}

const aggregateDaily = (txs: MockTransaction[]): DailyAggregate[] => {
    const map: Record<string, DailyAggregate> = {};
    txs.forEach(tx => {
        if (!map[tx.date]) {
            map[tx.date] = {
                date: tx.date,
                revenue: 0, expense: 0, adSpend: 0, refunds: 0,
                discounts: 0, shipping: 0, fees: 0, orders: 0, net: 0
            };
        }
        const row = map[tx.date];
        if (tx.type === 'revenue') {
            row.revenue += tx.amount;
            if (tx.category === 'product_sale') row.orders += 1;
        } else {
            row.expense += tx.amount;
            if (AD_CATEGORIES.has(tx.category)) row.adSpend += tx.amount;
            if (REFUND_CATEGORIES.has(tx.category)) row.refunds += tx.amount;
            if (DISCOUNT_CATEGORIES.has(tx.category)) row.discounts += tx.amount;
            if (SHIPPING_CATEGORIES.has(tx.category)) row.shipping += tx.amount;
            if (FEE_CATEGORIES.has(tx.category)) row.fees += tx.amount;
        }
        row.net = row.revenue - row.expense;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

const aggregateByCategory = (txs: MockTransaction[], type: 'revenue' | 'expense') => {
    const map: Record<string, number> = {};
    txs.forEach(tx => {
        if (tx.type !== type) return;
        map[tx.category] = (map[tx.category] || 0) + tx.amount;
    });
    return Object.entries(map)
        .map(([name, value], i) => ({
            name,
            value,
            color: CHART_COLORS.multi[i % CHART_COLORS.multi.length]
        }))
        .sort((a, b) => b.value - a.value);
};

const aggregateByEntity = (txs: MockTransaction[], entities: { id: string; name: string }[], type: 'revenue' | 'expense') => {
    const map: Record<string, number> = {};
    txs.forEach(tx => {
        if (tx.type !== type) return;
        map[tx.entityId] = (map[tx.entityId] || 0) + tx.amount;
    });
    return Object.entries(map)
        .map(([id, value]) => ({
            name: entities.find(e => e.id === id)?.name || id,
            value
        }))
        .sort((a, b) => b.value - a.value);
};

const aggregateBySource = (txs: MockTransaction[], type: 'revenue' | 'expense') => {
    const map: Record<string, number> = {};
    txs.forEach(tx => {
        if (tx.type !== type) return;
        map[tx.source] = (map[tx.source] || 0) + tx.amount;
    });
    return Object.entries(map)
        .map(([name, value], i) => ({ name, value, color: CHART_COLORS.multi[i % CHART_COLORS.multi.length] }))
        .sort((a, b) => b.value - a.value);
};

const dayOfWeekAggregate = (daily: DailyAggregate[]) => {
    const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const buckets: Record<number, { revenue: number; orders: number; count: number }> = {};
    daily.forEach(d => {
        const dow = getDay(parseISO(d.date));
        if (!buckets[dow]) buckets[dow] = { revenue: 0, orders: 0, count: 0 };
        buckets[dow].revenue += d.revenue;
        buckets[dow].orders += d.orders;
        buckets[dow].count += 1;
    });
    return labels.map((label, i) => ({
        day: label,
        revenue: buckets[i] ? buckets[i].revenue / Math.max(1, buckets[i].count) : 0,
        orders: buckets[i] ? buckets[i].orders / Math.max(1, buckets[i].count) : 0
    }));
};

// ─── Building blocks ────────────────────────────────────────────────────────

const ChartTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
    <div className="flex items-center justify-between mb-3 px-1">
        <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold truncate">{children}</span>
        {right}
    </div>
);

const KpiCard: React.FC<{
    label: string;
    value: string;
    color?: string;
    delta?: { value: string; positive?: boolean };
    sub?: string;
    icon?: React.ReactNode;
}> = ({ label, value, color = 'text-blue-600', delta, sub, icon }) => (
    <div className="flex flex-col items-center justify-center h-full px-2">
        <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold mb-1 flex items-center gap-1.5">
            {icon}
            {label}
        </span>
        <div className={`text-3xl font-bold ${color}`}>{value}</div>
        {delta && (
            <div className={`flex items-center gap-1 mt-1 text-[10px] font-medium ${delta.positive ? 'text-emerald-500/80' : 'text-red-500/80'}`}>
                {delta.positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                <span>{delta.value}</span>
            </div>
        )}
        {sub && <span className="text-[9px] text-neutral-400 mt-1">{sub}</span>}
    </div>
);

const formatMoney = (n: number) => `$${Math.round(n).toLocaleString()}`;
const formatShortMoney = (n: number) => {
    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
    return `$${Math.round(n)}`;
};

const TrendArea: React.FC<{
    title: string;
    data: any[];
    dataKey: string;
    color: string;
    badge?: string;
}> = ({ title, data, dataKey, color, badge }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle right={badge ? <span className="text-[10px] font-bold text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full">{badge}</span> : undefined}>{title}</ChartTitle>
        <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                        <linearGradient id={`grad-${dataKey}-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" hide />
                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Area type="monotone" dataKey={dataKey} stroke={color} fillOpacity={1} fill={`url(#grad-${dataKey}-${color.replace('#', '')})`} strokeWidth={2} />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const TrendLine: React.FC<{
    title: string;
    data: any[];
    dataKey: string;
    color: string;
    badge?: string;
    yFormatter?: (v: number) => string;
}> = ({ title, data, dataKey, color, badge, yFormatter }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle right={badge ? <span className="text-[10px] font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">{badge}</span> : undefined}>{title}</ChartTitle>
        <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" hide />
                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} tickFormatter={yFormatter} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const MultiSeriesArea: React.FC<{
    title: string;
    data: any[];
    series: { key: string; color: string; label: string }[];
    stacked?: boolean;
}> = ({ title, data, series, stacked }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle right={
            <div className="flex items-center gap-2">
                {series.map(s => (
                    <div key={s.key} className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-[9px] font-medium text-neutral-500">{s.label}</span>
                    </div>
                ))}
            </div>
        }>{title}</ChartTitle>
        <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" hide />
                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    {series.map(s => (
                        <Area
                            key={s.key}
                            type="monotone"
                            dataKey={s.key}
                            stroke={s.color}
                            fill={s.color}
                            fillOpacity={0.25}
                            strokeWidth={2}
                            stackId={stacked ? '1' : undefined}
                        />
                    ))}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const MultiSeriesLine: React.FC<{
    title: string;
    data: any[];
    series: { key: string; color: string; label: string }[];
}> = ({ title, data, series }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle right={
            <div className="flex items-center gap-2">
                {series.map(s => (
                    <div key={s.key} className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-[9px] font-medium text-neutral-500">{s.label}</span>
                    </div>
                ))}
            </div>
        }>{title}</ChartTitle>
        <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" hide />
                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    {series.map(s => (
                        <Line key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={2} dot={false} />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const SimpleBar: React.FC<{
    title: string;
    data: any[];
    dataKey: string;
    color: string;
    xKey?: string;
    showAxisLabels?: boolean;
}> = ({ title, data, dataKey, color, xKey = 'date', showAxisLabels }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle>{title}</ChartTitle>
        <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                    <XAxis dataKey={xKey} hide={!showAxisLabels} tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const PairedBars: React.FC<{
    title: string;
    data: any[];
    keys: { key: string; color: string; label: string }[];
}> = ({ title, data, keys }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle right={
            <div className="flex items-center gap-2">
                {keys.map(s => (
                    <div key={s.key} className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                        <span className="text-[9px] font-medium text-neutral-500">{s.label}</span>
                    </div>
                ))}
            </div>
        }>{title}</ChartTitle>
        <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="date" hide />
                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    {keys.map(s => (
                        <Bar key={s.key} dataKey={s.key} fill={s.color} radius={[3, 3, 0, 0]} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const HorizontalBar: React.FC<{
    title: string;
    rows: { name: string; value: number; color?: string }[];
    valueFormatter?: (v: number) => string;
    accent?: string;
}> = ({ title, rows, valueFormatter = formatShortMoney, accent = CHART_COLORS.net }) => {
    const max = Math.max(1, ...rows.map(r => Math.abs(r.value)));
    const top = rows.slice(0, 6);
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-1.5">
                {top.map((row, i) => {
                    const pct = (Math.abs(row.value) / max) * 100;
                    return (
                        <div key={`${row.name}-${i}`} className="flex items-center gap-2 text-[10px]">
                            <span className="w-20 truncate font-medium text-neutral-600">{row.name}</span>
                            <div className="flex-1 h-3 bg-neutral-100 rounded-full overflow-hidden relative">
                                <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: row.color || accent }} />
                            </div>
                            <span className="w-12 text-right font-bold text-neutral-700">{valueFormatter(row.value)}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Donut: React.FC<{
    title: string;
    data: { name: string; value: number; color: string }[];
    centerLabel?: string;
    centerValue?: string;
}> = ({ title, data, centerLabel, centerValue }) => {
    const top = data.slice(0, 6);
    const total = top.reduce((a, b) => a + b.value, 0);
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 flex items-center gap-3">
                <div className="w-1/2 h-full relative">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={top} cx="50%" cy="50%" innerRadius="60%" outerRadius="85%" paddingAngle={3} dataKey="value">
                                {top.map((entry, i) => <Cell key={`c-${i}`} fill={entry.color} />)}
                            </Pie>
                            <Tooltip contentStyle={TOOLTIP_STYLE} />
                        </PieChart>
                    </ResponsiveContainer>
                    {(centerLabel || centerValue) && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            {centerValue && <span className="text-sm font-bold text-neutral-700">{centerValue}</span>}
                            {centerLabel && <span className="text-[8px] uppercase tracking-widest text-neutral-400 font-bold">{centerLabel}</span>}
                        </div>
                    )}
                </div>
                <div className="w-1/2 space-y-1.5 overflow-hidden">
                    {top.map(cat => {
                        const pct = total > 0 ? (cat.value / total * 100) : 0;
                        return (
                            <div key={cat.name} className="flex items-center justify-between text-[10px]">
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                                    <span className="text-neutral-500 font-medium truncate">{cat.name}</span>
                                </div>
                                <span className="font-bold text-neutral-700 shrink-0">{pct.toFixed(0)}%</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const Funnel: React.FC<{ title: string; stages: { name: string; value: number; color: string }[] }> = ({ title, stages }) => {
    const max = Math.max(1, ...stages.map(s => s.value));
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 flex flex-col justify-center gap-1.5">
                {stages.map((stage, i) => {
                    const pct = (stage.value / max) * 100;
                    const conversion = i > 0 ? (stage.value / stages[i - 1].value * 100) : null;
                    return (
                        <div key={stage.name} className="flex items-center gap-2">
                            <span className="w-20 text-[10px] font-medium text-neutral-600 truncate">{stage.name}</span>
                            <div className="flex-1 h-5 bg-neutral-50 rounded-md relative overflow-hidden">
                                <div className="h-full rounded-md flex items-center px-2" style={{ width: `${pct}%`, backgroundColor: stage.color }}>
                                    <span className="text-[9px] font-bold text-white">{stage.value.toLocaleString()}</span>
                                </div>
                            </div>
                            {conversion !== null && (
                                <span className="w-12 text-right text-[9px] font-bold text-neutral-500">{conversion.toFixed(0)}%</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const Waterfall: React.FC<{ title: string; steps: { name: string; value: number }[] }> = ({ title, steps }) => {
    let runningTotal = 0;
    const bars = steps.map(s => {
        const start = runningTotal;
        runningTotal += s.value;
        return { name: s.name, start, end: runningTotal, value: s.value };
    });
    const max = Math.max(...bars.map(b => Math.max(b.start, b.end)));
    const min = Math.min(0, ...bars.map(b => Math.min(b.start, b.end)));
    const range = max - min || 1;
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 flex items-end gap-1 px-2">
                {bars.map((b, i) => {
                    const positive = b.value >= 0;
                    const top = Math.max(b.start, b.end);
                    const bottom = Math.min(b.start, b.end);
                    const heightPct = ((top - bottom) / range) * 100;
                    const offsetPct = ((max - top) / range) * 100;
                    return (
                        <div key={`${b.name}-${i}`} className="flex-1 flex flex-col items-center justify-end h-full">
                            <div className="w-full h-full relative">
                                <div
                                    className={`absolute left-0 right-0 rounded-md ${positive ? 'bg-emerald-500' : 'bg-red-500'} ${i === 0 || i === bars.length - 1 ? 'bg-blue-500' : ''}`}
                                    style={{ height: `${heightPct}%`, top: `${offsetPct}%` }}
                                />
                            </div>
                            <span className="text-[8px] font-medium text-neutral-500 mt-1 truncate w-full text-center">{b.name}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ScatterView: React.FC<{
    title: string;
    points: { x: number; y: number; z?: number; name?: string }[];
    xLabel?: string;
    yLabel?: string;
}> = ({ title, points, xLabel, yLabel }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle right={xLabel && yLabel ? <span className="text-[9px] text-neutral-400">{yLabel} vs {xLabel}</span> : undefined}>{title}</ChartTitle>
        <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="x" tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <YAxis dataKey="y" tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                    <ZAxis dataKey="z" range={[40, 200]} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter data={points} fill={CHART_COLORS.accent} fillOpacity={0.7} />
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    </div>
);

const TableView: React.FC<{
    title: string;
    columns: string[];
    rows: (string | number)[][];
}> = ({ title, columns, rows }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle>{title}</ChartTitle>
        <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-[10px]">
                <thead>
                    <tr className="border-b border-neutral-200">
                        {columns.map(c => (
                            <th key={c} className="text-left font-bold text-neutral-500 uppercase tracking-wider py-1.5 px-2">{c}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.slice(0, 8).map((row, i) => (
                        <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                            {row.map((cell, j) => (
                                <td key={j} className={`py-1.5 px-2 ${j === 0 ? 'font-medium text-neutral-700' : 'text-neutral-600'}`}>{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const Heatmap: React.FC<{
    title: string;
    rows: string[];
    cols: string[];
    values: number[][];
}> = ({ title, rows, cols, values }) => {
    const max = Math.max(...values.flat(), 1);
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 overflow-hidden">
                <table className="w-full h-full text-[9px]">
                    <thead>
                        <tr>
                            <th />
                            {cols.map(c => <th key={c} className="text-neutral-500 font-bold uppercase tracking-wider">{c}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r}>
                                <td className="text-neutral-500 font-bold uppercase tracking-wider pr-2">{r}</td>
                                {cols.map((_, j) => {
                                    const v = values[i]?.[j] || 0;
                                    const intensity = v / max;
                                    return (
                                        <td key={j} className="p-0.5">
                                            <div
                                                className="w-full h-5 rounded"
                                                style={{ backgroundColor: `rgba(16, 185, 129, ${0.1 + intensity * 0.7})` }}
                                                title={`${formatShortMoney(v)}`}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const EventTimeline: React.FC<{
    title: string;
    items: { date: string; name: string; impact?: 'high' | 'medium' | 'low'; type?: string }[];
}> = ({ title, items }) => (
    <div className="w-full h-full flex flex-col p-2">
        <ChartTitle>{title}</ChartTitle>
        <div className="flex-1 min-h-0 overflow-auto space-y-1.5">
            {items.slice(0, 6).map((it, i) => {
                const tone = it.impact === 'high' ? 'bg-red-500' : it.impact === 'low' ? 'bg-emerald-500' : 'bg-amber-500';
                return (
                    <div key={i} className="flex items-start gap-2 text-[10px]">
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${tone}`} />
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-neutral-700 truncate">{it.name}</div>
                            <div className="text-neutral-400">{it.date}{it.type ? ` · ${it.type}` : ''}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
);

const BeforeAfter: React.FC<{
    title: string;
    label: string;
    before: number;
    after: number;
    valueFormatter?: (v: number) => string;
}> = ({ title, label, before, after, valueFormatter = formatShortMoney }) => {
    const delta = after - before;
    const pct = before > 0 ? (delta / before * 100) : 0;
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center">
                <div className="flex items-end gap-4">
                    <div className="flex flex-col items-center">
                        <div className="text-[9px] font-bold text-neutral-400 uppercase tracking-widest">Before</div>
                        <div className="text-lg font-bold text-neutral-500">{valueFormatter(before)}</div>
                    </div>
                    <div className="text-neutral-300 text-2xl font-light pb-1">→</div>
                    <div className="flex flex-col items-center">
                        <div className="text-[9px] font-bold text-neutral-400 uppercase tracking-widest">After</div>
                        <div className={`text-lg font-bold ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{valueFormatter(after)}</div>
                    </div>
                </div>
                <div className={`mt-3 px-3 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 ${delta >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {delta >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {pct.toFixed(1)}% {label}
                </div>
            </div>
        </div>
    );
};

const StatusList: React.FC<{
    title: string;
    items: { name: string; status: 'ok' | 'warn' | 'error' | 'pending'; meta?: string }[];
}> = ({ title, items }) => {
    const iconFor = (s: string) => {
        if (s === 'ok') return <CheckCircle2 size={12} className="text-emerald-500" />;
        if (s === 'warn') return <AlertTriangle size={12} className="text-amber-500" />;
        if (s === 'error') return <AlertTriangle size={12} className="text-red-500" />;
        return <Clock size={12} className="text-neutral-400" />;
    };
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 overflow-auto space-y-1">
                {items.slice(0, 6).map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-[10px] py-1 border-b border-neutral-100 last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                            {iconFor(it.status)}
                            <span className="font-medium text-neutral-700 truncate">{it.name}</span>
                        </div>
                        {it.meta && <span className="text-neutral-400 shrink-0">{it.meta}</span>}
                    </div>
                ))}
            </div>
        </div>
    );
};

const SankeyMini: React.FC<{ title: string; flows: { from: string; to: string; value: number; color: string }[] }> = ({ title, flows }) => {
    const total = flows.reduce((a, b) => a + b.value, 0);
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 flex items-center gap-3">
                <div className="flex-1 flex flex-col gap-1.5">
                    {flows.map((f, i) => {
                        const pct = total > 0 ? (f.value / total * 100) : 0;
                        return (
                            <div key={i} className="flex items-center gap-2 text-[10px]">
                                <span className="w-16 truncate font-medium text-neutral-600 text-right">{f.from}</span>
                                <div className="flex-1 h-2 rounded-full" style={{ background: `linear-gradient(to right, ${f.color}66, ${f.color})`, width: `${Math.max(20, pct)}%` }} />
                                <span className="w-16 truncate font-medium text-neutral-600">{f.to}</span>
                                <span className="w-14 text-right font-bold text-neutral-700">{formatShortMoney(f.value)}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const Comparison: React.FC<{
    title: string;
    label: string;
    current: number;
    previous: number;
    valueFormatter?: (v: number) => string;
}> = ({ title, label, current, previous, valueFormatter = formatMoney }) => {
    const delta = current - previous;
    const pct = previous > 0 ? (delta / previous * 100) : 0;
    return (
        <div className="w-full h-full flex flex-col p-2">
            <ChartTitle>{title}</ChartTitle>
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2">
                <div className={`text-3xl font-bold ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{valueFormatter(current)}</div>
                <div className="text-[10px] text-neutral-400 font-medium">vs {valueFormatter(previous)} {label}</div>
                <div className={`px-3 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 ${delta >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {delta >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {Math.abs(pct).toFixed(1)}%
                </div>
            </div>
        </div>
    );
};

// ─── Main Renderer ──────────────────────────────────────────────────────────

export const ChartRenderer: React.FC<ChartRendererProps> = ({ type, mockData }) => {
    const { t } = useTranslation('common');

    const daily = useMemo(() => mockData ? aggregateDaily(mockData.transactions) : [], [mockData]);
    const expenseCategories = useMemo(() => mockData ? aggregateByCategory(mockData.transactions, 'expense') : [], [mockData]);
    const topRevenueEntities = useMemo(() => mockData ? aggregateByEntity(mockData.transactions, mockData.entities, 'revenue') : [], [mockData]);
    const sourceRevenue = useMemo(() => mockData ? aggregateBySource(mockData.transactions, 'revenue') : [], [mockData]);
    const dowAggregate = useMemo(() => dayOfWeekAggregate(daily), [daily]);

    if (!mockData) {
        return (
            <div className="flex items-center justify-center h-full text-neutral-400">
                <Activity size={24} className="animate-pulse opacity-20" />
            </div>
        );
    }

    const title = (key: string) => t(`charts.titles.${key}`, { defaultValue: key.replace(/_/g, ' ') });

    const totals = mockData.summary;
    const adSpend = daily.reduce((a, b) => a + b.adSpend, 0);
    const ordersTotal = daily.reduce((a, b) => a + b.orders, 0);
    const refundsTotal = daily.reduce((a, b) => a + b.refunds, 0);
    const shippingTotal = daily.reduce((a, b) => a + b.shipping, 0);
    const feesTotal = daily.reduce((a, b) => a + b.fees, 0);
    const aovValue = ordersTotal > 0 ? totals.totalRevenue / ordersTotal : 0;
    const roasValue = adSpend > 0 ? totals.totalRevenue / adSpend : 0;
    const cpaValue = ordersTotal > 0 ? adSpend / ordersTotal : 0;
    const previousRevenue = totals.totalRevenue * (0.85 + seededRandom(totals.totalRevenue) * 0.2);

    const renderChartContent = (): React.ReactNode => {
        switch (type) {
            // ── Basic KPIs ───────────────────────────────────────────────
            case CHART_TYPES.TOTAL_REVENUE:
                return <KpiCard label={title('total_revenue')} value={formatMoney(totals.totalRevenue)} color="text-emerald-600" delta={{ value: '+12.5%', positive: true }} icon={<DollarSign size={11} />} />;
            case CHART_TYPES.TOTAL_EXPENSES:
                return <KpiCard label={title('total_expenses')} value={formatMoney(totals.totalExpenses)} color="text-red-600" delta={{ value: '+4.2%', positive: false }} icon={<DollarSign size={11} />} />;
            case CHART_TYPES.NET_PROFIT_KPI: {
                const positive = totals.netIncome > 0;
                return <KpiCard label={title('net_profit_kpi')} value={formatMoney(totals.netIncome)} color={positive ? 'text-blue-600' : 'text-red-600'} delta={{ value: positive ? '+8.3%' : '-2.1%', positive }} />;
            }
            case CHART_TYPES.ORDERS_KPI:
                return <KpiCard label={title('orders_kpi')} value={ordersTotal.toLocaleString()} color="text-violet-600" delta={{ value: '+6.1%', positive: true }} icon={<ShoppingBag size={11} />} />;
            case CHART_TYPES.AD_SPEND_KPI:
                return <KpiCard label={title('ad_spend_kpi')} value={formatMoney(adSpend)} color="text-amber-600" delta={{ value: '+9.4%', positive: false }} icon={<Target size={11} />} />;
            case CHART_TYPES.AOV_KPI:
                return <KpiCard label={title('aov_kpi')} value={formatMoney(aovValue)} color="text-violet-600" sub="Average per Order" />;

            // ── Profitability ────────────────────────────────────────────
            case CHART_TYPES.NET_PROFIT_OVER_TIME:
                return <TrendArea title={title('net_profit_over_time')} data={daily} dataKey="net" color={CHART_COLORS.net} />;
            case CHART_TYPES.PROFIT_WATERFALL:
                return <Waterfall title={title('profit_waterfall')} steps={[
                    { name: 'Revenue', value: totals.totalRevenue },
                    { name: 'COGS', value: -totals.totalRevenue * 0.35 },
                    { name: 'Marketing', value: -adSpend },
                    { name: 'Shipping', value: -shippingTotal },
                    { name: 'Fees', value: -feesTotal },
                    { name: 'Refunds', value: -refundsTotal },
                    { name: 'Net', value: totals.netIncome }
                ]} />;
            case CHART_TYPES.PROFIT_MARGIN_OVER_TIME: {
                const data = daily.map(d => ({ ...d, marginPct: d.revenue > 0 ? (d.net / d.revenue * 100) : 0 }));
                return <TrendLine title={title('profit_margin_over_time')} data={data} dataKey="marginPct" color={CHART_COLORS.accent} yFormatter={v => `${v.toFixed(0)}%`} badge={`${(totals.totalRevenue > 0 ? totals.netIncome / totals.totalRevenue * 100 : 0).toFixed(0)}% avg`} />;
            }
            case CHART_TYPES.INCOME_VS_COSTS:
                return <MultiSeriesArea title={title('income_vs_costs')} data={daily} series={[
                    { key: 'revenue', color: CHART_COLORS.revenue, label: 'Revenue' },
                    { key: 'expense', color: CHART_COLORS.expense, label: 'Costs' }
                ]} />;
            case CHART_TYPES.DAILY_PL_TABLE:
                return <TableView title={title('daily_pl_table')} columns={['Date', 'Revenue', 'Expenses', 'Net']} rows={daily.slice(-8).map(d => [
                    format(parseISO(d.date), 'MMM dd'),
                    formatShortMoney(d.revenue),
                    formatShortMoney(d.expense),
                    formatShortMoney(d.net)
                ])} />;
            case CHART_TYPES.RUNNING_PROFIT: {
                let acc = 0;
                const data = daily.map(d => { acc += d.net; return { date: d.date, running: acc }; });
                return <TrendArea title={title('running_profit')} data={data} dataKey="running" color={CHART_COLORS.net} />;
            }
            case CHART_TYPES.PROFIT_PERIOD_COMPARISON:
                return <Comparison title={title('profit_period_comparison')} label="last period" current={totals.netIncome} previous={totals.netIncome * 0.92} />;

            // ── Ad Performance ───────────────────────────────────────────
            case CHART_TYPES.ROAS_BY_PLATFORM:
                return <HorizontalBar title={title('roas_by_platform')} accent={CHART_COLORS.accent} valueFormatter={v => `${v.toFixed(1)}x`} rows={[
                    { name: 'Meta', value: 4.2 },
                    { name: 'Google', value: 3.6 },
                    { name: 'TikTok', value: 2.9 },
                    { name: 'Amazon', value: 2.4 },
                    { name: 'Direct', value: 5.1 }
                ]} />;
            case CHART_TYPES.ROAS_OVER_TIME: {
                const data = daily.map((d, i) => ({ date: d.date, roas: 2 + Math.sin(i / 3) * 1.2 + seededRandom(i) * 0.6 }));
                return <TrendLine title={title('roas_over_time')} data={data} dataKey="roas" color={CHART_COLORS.accent} badge={`${roasValue.toFixed(1)}x avg`} yFormatter={v => `${v.toFixed(1)}x`} />;
            }
            case CHART_TYPES.BLENDED_MER:
                return <KpiCard label={title('blended_mer')} value={`${roasValue.toFixed(2)}x`} color="text-violet-600" delta={{ value: '+0.4x', positive: true }} sub={`$${formatShortMoney(adSpend)} ad spend`} />;
            case CHART_TYPES.AD_SPEND_PERCENT: {
                const data = daily.map(d => ({ date: d.date, pct: d.revenue > 0 ? (d.adSpend / d.revenue * 100) : 0 }));
                return <TrendLine title={title('ad_spend_percent')} data={data} dataKey="pct" color={CHART_COLORS.warning} yFormatter={v => `${v.toFixed(0)}%`} />;
            }
            case CHART_TYPES.CPA_OVER_TIME: {
                const data = daily.map(d => ({ date: d.date, cpa: d.orders > 0 ? d.adSpend / d.orders : 0 }));
                return <TrendLine title={title('cpa_over_time')} data={data} dataKey="cpa" color={CHART_COLORS.warning} badge={formatMoney(cpaValue)} />;
            }
            case CHART_TYPES.CAMPAIGN_ROAS_LEADERBOARD:
                return <HorizontalBar title={title('campaign_roas_leaderboard')} valueFormatter={v => `${v.toFixed(1)}x`} accent={CHART_COLORS.accent} rows={[
                    { name: 'Spring Promo', value: 5.4 },
                    { name: 'Brand Awareness', value: 4.1 },
                    { name: 'Retargeting', value: 6.8 },
                    { name: 'Lookalike', value: 3.2 },
                    { name: 'New Audience', value: 2.5 },
                    { name: 'Catalog Ads', value: 4.7 }
                ]} />;
            case CHART_TYPES.AD_SET_EFFICIENCY_SCATTER:
                return <ScatterView title={title('ad_set_efficiency_scatter')} xLabel="Spend" yLabel="ROAS" points={Array.from({ length: 24 }).map((_, i) => ({
                    x: 100 + seededRandom(i * 3) * 1500,
                    y: 1 + seededRandom(i * 7) * 5,
                    z: 50 + seededRandom(i * 11) * 200
                }))} />;
            case CHART_TYPES.CREATIVE_PERFORMANCE_TABLE:
                return <TableView title={title('creative_performance_table')} columns={['Creative', 'CTR', 'ROAS', 'Spend']} rows={[
                    ['Lifestyle 01', '2.4%', '4.8x', formatShortMoney(420)],
                    ['Product close-up', '3.1%', '6.2x', formatShortMoney(580)],
                    ['Testimonial', '1.9%', '3.4x', formatShortMoney(320)],
                    ['UGC reel', '4.2%', '5.1x', formatShortMoney(710)],
                    ['Carousel A', '2.0%', '2.9x', formatShortMoney(290)]
                ]} />;
            case CHART_TYPES.AD_FUNNEL:
                return <Funnel title={title('ad_funnel')} stages={[
                    { name: 'Impressions', value: 240000, color: '#a78bfa' },
                    { name: 'Clicks', value: 8400, color: '#8b5cf6' },
                    { name: 'Add to Cart', value: 1820, color: '#7c3aed' },
                    { name: 'Orders', value: 410, color: '#6d28d9' }
                ]} />;
            case CHART_TYPES.CPM_TREND: {
                const data = daily.map((d, i) => ({ date: d.date, cpm: 8 + Math.sin(i / 4) * 3 + seededRandom(i) * 2 }));
                return <TrendLine title={title('cpm_trend')} data={data} dataKey="cpm" color={CHART_COLORS.info} yFormatter={v => `$${v.toFixed(1)}`} />;
            }
            case CHART_TYPES.CPC_TREND: {
                const data = daily.map((d, i) => ({ date: d.date, cpc: 0.6 + Math.sin(i / 5) * 0.3 + seededRandom(i) * 0.2 }));
                return <TrendLine title={title('cpc_trend')} data={data} dataKey="cpc" color={CHART_COLORS.info} yFormatter={v => `$${v.toFixed(2)}`} />;
            }
            case CHART_TYPES.WASTED_AD_SPEND:
                return <KpiCard label={title('wasted_ad_spend')} value={formatMoney(adSpend * 0.18)} color="text-red-600" delta={{ value: '18% of spend', positive: false }} icon={<AlertTriangle size={11} />} />;
            case CHART_TYPES.CTR_OVER_TIME: {
                const data = daily.map((d, i) => ({ date: d.date, ctr: 1.5 + Math.sin(i / 6) * 1 + seededRandom(i) * 0.6 }));
                return <TrendLine title={title('ctr_over_time')} data={data} dataKey="ctr" color={CHART_COLORS.info} yFormatter={v => `${v.toFixed(1)}%`} />;
            }

            // ── Product Performance ──────────────────────────────────────
            case CHART_TYPES.BEST_SELLERS_BY_REVENUE:
                return <HorizontalBar title={title('best_sellers_by_revenue')} accent={CHART_COLORS.revenue} rows={topRevenueEntities} />;
            case CHART_TYPES.REVENUE_CONCENTRATION: {
                const sorted = [...topRevenueEntities].sort((a, b) => b.value - a.value);
                const total = sorted.reduce((a, b) => a + b.value, 0);
                let acc = 0;
                const data = sorted.map((e, i) => { acc += e.value; return { date: e.name, share: total > 0 ? (acc / total * 100) : 0, x: i }; });
                return <TrendLine title={title('revenue_concentration')} data={data} dataKey="share" color={CHART_COLORS.accent} badge="80/20" yFormatter={v => `${v.toFixed(0)}%`} />;
            }
            case CHART_TYPES.PRODUCT_SALES_TREND: {
                const data = daily.map((d, i) => ({
                    date: d.date,
                    product1: d.revenue * (0.4 + Math.sin(i / 3) * 0.2),
                    product2: d.revenue * (0.3 + Math.cos(i / 4) * 0.15),
                    product3: d.revenue * (0.2 + seededRandom(i) * 0.1)
                }));
                return <MultiSeriesLine title={title('product_sales_trend')} data={data} series={[
                    { key: 'product1', color: CHART_COLORS.revenue, label: 'Top SKU' },
                    { key: 'product2', color: CHART_COLORS.net, label: '#2 SKU' },
                    { key: 'product3', color: CHART_COLORS.accent, label: '#3 SKU' }
                ]} />;
            }
            case CHART_TYPES.UNITS_SOLD_RANKING:
                return <HorizontalBar title={title('units_sold_ranking')} valueFormatter={v => `${Math.round(v)} units`} rows={topRevenueEntities.map(e => ({ name: e.name, value: Math.round(e.value / 50) }))} />;
            case CHART_TYPES.REVENUE_PER_UNIT:
                return <HorizontalBar title={title('revenue_per_unit')} accent={CHART_COLORS.accent} rows={topRevenueEntities.map(e => ({ name: e.name, value: e.value / Math.max(1, Math.round(e.value / 50)) }))} />;
            case CHART_TYPES.PRODUCT_RETURN_RATE:
                return <HorizontalBar title={title('product_return_rate')} valueFormatter={v => `${v.toFixed(1)}%`} accent={CHART_COLORS.expense} rows={topRevenueEntities.map((e, i) => ({ name: e.name, value: 2 + seededRandom(i) * 8 }))} />;
            case CHART_TYPES.REFUND_DAMAGE_BY_PRODUCT:
                return <HorizontalBar title={title('refund_damage_by_product')} accent={CHART_COLORS.expense} rows={topRevenueEntities.map((e, i) => ({ name: e.name, value: e.value * (0.05 + seededRandom(i) * 0.1) }))} />;
            case CHART_TYPES.PRODUCT_CHANNEL_MATRIX:
                return <Heatmap title={title('product_channel_matrix')} rows={topRevenueEntities.slice(0, 5).map(e => e.name.slice(0, 8))} cols={['Shopify', 'Amazon', 'Direct', 'Stripe']} values={topRevenueEntities.slice(0, 5).map((_, i) => [
                    1000 + seededRandom(i * 11) * 5000,
                    500 + seededRandom(i * 13) * 3000,
                    300 + seededRandom(i * 17) * 2500,
                    200 + seededRandom(i * 19) * 1500
                ])} />;

            // ── Cost Breakdown ───────────────────────────────────────────
            case CHART_TYPES.EXPENSE_CATEGORY_DONUT:
                return <Donut title={title('expense_category_donut')} data={expenseCategories} centerValue={formatShortMoney(totals.totalExpenses)} centerLabel="Total" />;
            case CHART_TYPES.EXPENSE_CATEGORY_TREND: {
                const cats = expenseCategories.slice(0, 4).map(c => c.name);
                const data = daily.map((d, i) => {
                    const out: any = { date: d.date };
                    cats.forEach((c, j) => { out[c] = (d.expense / cats.length) * (0.5 + seededRandom(i + j * 7)); });
                    return out;
                });
                return <MultiSeriesArea title={title('expense_category_trend')} data={data} stacked series={cats.map((c, i) => ({ key: c, color: CHART_COLORS.multi[i], label: c }))} />;
            }
            case CHART_TYPES.PAYMENT_PROCESSING_FEES:
                return <KpiCard label={title('payment_processing_fees')} value={formatMoney(feesTotal)} color="text-amber-600" sub={`${(totals.totalRevenue > 0 ? feesTotal / totals.totalRevenue * 100 : 0).toFixed(1)}% of revenue`} />;
            case CHART_TYPES.PAYMENT_FEES_PERCENT: {
                const data = daily.map(d => ({ date: d.date, pct: d.revenue > 0 ? (d.fees / d.revenue * 100) : 0 }));
                return <TrendLine title={title('payment_fees_percent')} data={data} dataKey="pct" color={CHART_COLORS.warning} yFormatter={v => `${v.toFixed(2)}%`} />;
            }
            case CHART_TYPES.SHIPPING_COST_OVER_TIME:
                return <TrendArea title={title('shipping_cost_over_time')} data={daily} dataKey="shipping" color={CHART_COLORS.info} />;
            case CHART_TYPES.SHIPPING_PER_ORDER:
                return <KpiCard label={title('shipping_per_order')} value={formatMoney(ordersTotal > 0 ? shippingTotal / ordersTotal : 0)} color="text-cyan-600" sub="Avg per order" />;
            case CHART_TYPES.DISCOUNT_TOTAL_AND_RATE:
                return <PairedBars title={title('discount_total_and_rate')} data={daily} keys={[
                    { key: 'discounts', color: CHART_COLORS.warning, label: 'Discounts' }
                ]} />;
            case CHART_TYPES.JOURNAL_EXPENSE_BREAKDOWN:
                return <Donut title={title('journal_expense_breakdown')} data={expenseCategories.slice(2)} centerValue={formatShortMoney(expenseCategories.slice(2).reduce((a, b) => a + b.value, 0))} centerLabel="Custom" />;
            case CHART_TYPES.JOURNAL_EXPENSES_OVER_TIME:
                return <TrendArea title={title('journal_expenses_over_time')} data={daily} dataKey="expense" color={CHART_COLORS.expense} />;
            case CHART_TYPES.TRUE_COST_PER_ORDER:
                return <KpiCard label={title('true_cost_per_order')} value={formatMoney(ordersTotal > 0 ? totals.totalExpenses / ordersTotal : 0)} color="text-red-600" sub="Including all costs" />;
            case CHART_TYPES.EXPENSE_ANOMALY_DETECTOR: {
                const data = daily.map((d, i) => ({ date: d.date, expense: d.expense, anomaly: i === Math.floor(daily.length * 0.7) ? d.expense * 2 : null }));
                return (
                    <div className="w-full h-full flex flex-col p-2 bg-red-50/30">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-red-100 rounded-lg text-red-600"><AlertTriangle size={14} /></div>
                            <div>
                                <span className="block text-[10px] uppercase tracking-widest text-red-400 font-bold leading-none">{title('expense_anomaly_detector')}</span>
                                <span className="text-[11px] font-bold text-red-700">Spike detected</span>
                            </div>
                        </div>
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                                    <XAxis dataKey="date" hide />
                                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                                    <Line type="monotone" dataKey="expense" stroke={CHART_COLORS.expense} strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="anomaly" stroke={CHART_COLORS.warning} strokeWidth={0} dot={{ r: 6, fill: CHART_COLORS.warning }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );
            }

            // ── Cash Flow ────────────────────────────────────────────────
            case CHART_TYPES.DAILY_INFLOWS_OUTFLOWS:
                return <PairedBars title={title('daily_inflows_outflows')} data={daily} keys={[
                    { key: 'revenue', color: CHART_COLORS.revenue, label: 'In' },
                    { key: 'expense', color: CHART_COLORS.expense, label: 'Out' }
                ]} />;
            case CHART_TYPES.CUMULATIVE_CASH_POSITION: {
                let acc = 0;
                const data = daily.map(d => { acc += d.net; return { date: d.date, cash: acc }; });
                return <TrendArea title={title('cumulative_cash_position')} data={data} dataKey="cash" color={CHART_COLORS.net} />;
            }
            case CHART_TYPES.AD_SPEND_VS_REVENUE_TIMING:
                return <MultiSeriesLine title={title('ad_spend_vs_revenue_timing')} data={daily} series={[
                    { key: 'adSpend', color: CHART_COLORS.warning, label: 'Ad Spend' },
                    { key: 'revenue', color: CHART_COLORS.revenue, label: 'Revenue' }
                ]} />;
            case CHART_TYPES.WEEKLY_CASH_SUMMARY: {
                const weeks: { date: string; net: number }[] = [];
                for (let i = 0; i < daily.length; i += 7) {
                    const slice = daily.slice(i, i + 7);
                    weeks.push({ date: `W${weeks.length + 1}`, net: slice.reduce((a, b) => a + b.net, 0) });
                }
                return <SimpleBar title={title('weekly_cash_summary')} data={weeks} dataKey="net" color={CHART_COLORS.net} xKey="date" showAxisLabels />;
            }
            case CHART_TYPES.BREAK_EVEN_DAY: {
                let acc = 0;
                const breakEvenIdx = daily.findIndex(d => { acc += d.net; return acc > 0; });
                return <KpiCard label={title('break_even_day')} value={breakEvenIdx >= 0 ? `Day ${breakEvenIdx + 1}` : 'Not yet'} color="text-emerald-600" sub={breakEvenIdx >= 0 ? `${daily[breakEvenIdx]?.date}` : 'Still running'} />;
            }
            case CHART_TYPES.MONEY_FLOW_SANKEY:
                return <SankeyMini title={title('money_flow_sankey')} flows={[
                    { from: 'Sales', to: 'Revenue', value: totals.totalRevenue, color: CHART_COLORS.revenue },
                    { from: 'Revenue', to: 'COGS', value: totals.totalRevenue * 0.35, color: CHART_COLORS.expense },
                    { from: 'Revenue', to: 'Marketing', value: adSpend, color: CHART_COLORS.warning },
                    { from: 'Revenue', to: 'Shipping', value: shippingTotal, color: CHART_COLORS.info },
                    { from: 'Revenue', to: 'Net Profit', value: totals.netIncome, color: CHART_COLORS.net }
                ]} />;

            // ── Refunds ──────────────────────────────────────────────────
            case CHART_TYPES.RETURN_RATE_OVER_TIME: {
                const data = daily.map(d => ({ date: d.date, rate: d.revenue > 0 ? (d.refunds / d.revenue * 100) : 0 }));
                return <TrendLine title={title('return_rate_over_time')} data={data} dataKey="rate" color={CHART_COLORS.expense} yFormatter={v => `${v.toFixed(1)}%`} />;
            }
            case CHART_TYPES.REFUND_AMOUNT_OVER_TIME:
                return <TrendArea title={title('refund_amount_over_time')} data={daily} dataKey="refunds" color={CHART_COLORS.expense} />;
            case CHART_TYPES.REFUND_TYPE_BREAKDOWN:
                return <Donut title={title('refund_type_breakdown')} data={[
                    { name: 'Damaged', value: refundsTotal * 0.4, color: CHART_COLORS.expense },
                    { name: 'Wrong item', value: refundsTotal * 0.25, color: CHART_COLORS.warning },
                    { name: 'Cancelled', value: refundsTotal * 0.2, color: CHART_COLORS.info },
                    { name: 'Other', value: refundsTotal * 0.15, color: CHART_COLORS.text }
                ]} />;
            case CHART_TYPES.REFUND_TRUE_COST:
                return <KpiCard label={title('refund_true_cost')} value={formatMoney(refundsTotal * 1.4)} color="text-red-600" sub="Including processing & restocking" />;
            case CHART_TYPES.MOST_RETURNED_PRODUCTS:
                return <HorizontalBar title={title('most_returned_products')} accent={CHART_COLORS.expense} valueFormatter={v => `${Math.round(v)}`} rows={topRevenueEntities.map((e, i) => ({ name: e.name, value: 5 + seededRandom(i) * 30 }))} />;

            // ── Traffic & Attribution ────────────────────────────────────
            case CHART_TYPES.PAID_VS_ORGANIC_REVENUE:
                return <Donut title={title('paid_vs_organic_revenue')} data={[
                    { name: 'Paid', value: totals.totalRevenue * 0.62, color: CHART_COLORS.warning },
                    { name: 'Organic', value: totals.totalRevenue * 0.38, color: CHART_COLORS.revenue }
                ]} />;
            case CHART_TYPES.PAID_VS_ORGANIC_TREND: {
                const data = daily.map(d => ({ date: d.date, paid: d.revenue * 0.6, organic: d.revenue * 0.4 }));
                return <MultiSeriesArea title={title('paid_vs_organic_trend')} data={data} stacked series={[
                    { key: 'paid', color: CHART_COLORS.warning, label: 'Paid' },
                    { key: 'organic', color: CHART_COLORS.revenue, label: 'Organic' }
                ]} />;
            }
            case CHART_TYPES.REVENUE_BY_UTM_SOURCE:
                return <HorizontalBar title={title('revenue_by_utm_source')} accent={CHART_COLORS.accent} rows={sourceRevenue} />;
            case CHART_TYPES.REVENUE_BY_UTM_CAMPAIGN:
                return <HorizontalBar title={title('revenue_by_utm_campaign')} accent={CHART_COLORS.accent} rows={[
                    { name: 'spring_promo', value: totals.totalRevenue * 0.18 },
                    { name: 'retargeting_q2', value: totals.totalRevenue * 0.14 },
                    { name: 'brand_search', value: totals.totalRevenue * 0.22 },
                    { name: 'lookalike_v3', value: totals.totalRevenue * 0.09 },
                    { name: 'creator_collab', value: totals.totalRevenue * 0.11 },
                    { name: 'untagged', value: totals.totalRevenue * 0.26 }
                ]} />;
            case CHART_TYPES.UNATTRIBUTED_REVENUE:
                return <KpiCard label={title('unattributed_revenue')} value={formatMoney(totals.totalRevenue * 0.18)} color="text-neutral-600" sub="18% of total" icon={<AlertTriangle size={11} />} />;

            // ── Customer Insights ────────────────────────────────────────
            case CHART_TYPES.PURCHASE_FREQUENCY: {
                const data = [
                    { date: '1', value: 240 },
                    { date: '2', value: 120 },
                    { date: '3', value: 78 },
                    { date: '4', value: 42 },
                    { date: '5+', value: 30 }
                ];
                return <SimpleBar title={title('purchase_frequency')} data={data} dataKey="value" color={CHART_COLORS.accent} xKey="date" showAxisLabels />;
            }
            case CHART_TYPES.TOP_CUSTOMERS_BY_REVENUE:
                return <HorizontalBar title={title('top_customers_by_revenue')} accent={CHART_COLORS.revenue} rows={topRevenueEntities} />;
            case CHART_TYPES.CUSTOMER_CONCENTRATION:
                return <Donut title={title('customer_concentration')} data={[
                    { name: 'Top 10%', value: totals.totalRevenue * 0.45, color: CHART_COLORS.expense },
                    { name: 'Next 20%', value: totals.totalRevenue * 0.3, color: CHART_COLORS.warning },
                    { name: 'Bottom 70%', value: totals.totalRevenue * 0.25, color: CHART_COLORS.revenue }
                ]} centerValue="45%" centerLabel="from top 10" />;
            case CHART_TYPES.NEW_VS_RETURNING_REVENUE:
                return <Donut title={title('new_vs_returning_revenue')} data={[
                    { name: 'New', value: totals.totalRevenue * 0.55, color: CHART_COLORS.net },
                    { name: 'Returning', value: totals.totalRevenue * 0.45, color: CHART_COLORS.accent }
                ]} />;
            case CHART_TYPES.AVG_REVENUE_PER_CUSTOMER:
                return <KpiCard label={title('avg_revenue_per_customer')} value={formatMoney(totals.totalRevenue / Math.max(1, mockData.entities.length))} color="text-blue-600" sub="LTV estimate" />;

            // ── Growth & Scaling ─────────────────────────────────────────
            case CHART_TYPES.REVENUE_PERIOD_COMPARISON:
                return <Comparison title={title('revenue_period_comparison')} label="last period" current={totals.totalRevenue} previous={previousRevenue} />;
            case CHART_TYPES.ORDER_VOLUME_OVER_TIME:
                return <SimpleBar title={title('order_volume_over_time')} data={daily} dataKey="orders" color={CHART_COLORS.net} />;
            case CHART_TYPES.AOV_TREND: {
                const data = daily.map(d => ({ date: d.date, aov: d.orders > 0 ? d.revenue / d.orders : 0 }));
                return <TrendLine title={title('aov_trend')} data={data} dataKey="aov" color={CHART_COLORS.accent} />;
            }
            case CHART_TYPES.REVENUE_VS_AD_SPEND_GROWTH:
                return <MultiSeriesLine title={title('revenue_vs_ad_spend_growth')} data={daily} series={[
                    { key: 'revenue', color: CHART_COLORS.revenue, label: 'Revenue' },
                    { key: 'adSpend', color: CHART_COLORS.warning, label: 'Ad Spend' }
                ]} />;
            case CHART_TYPES.SPEND_VS_ROAS_SCATTER:
                return <ScatterView title={title('spend_vs_roas_scatter')} xLabel="Spend" yLabel="ROAS" points={daily.map((d, i) => ({
                    x: d.adSpend,
                    y: d.adSpend > 0 ? d.revenue / d.adSpend : 0,
                    z: 30 + seededRandom(i) * 100
                }))} />;
            case CHART_TYPES.SALES_BY_DAY_OF_WEEK:
                return <SimpleBar title={title('sales_by_day_of_week')} data={dowAggregate.map(d => ({ date: d.day, value: d.revenue }))} dataKey="value" color={CHART_COLORS.revenue} xKey="date" showAxisLabels />;

            // ── Forecasting ──────────────────────────────────────────────
            case CHART_TYPES.REVENUE_FORECAST: {
                const data = daily.map(d => ({ date: d.date, actual: d.revenue, forecast: null as number | null }));
                const last = daily[daily.length - 1]?.revenue || 0;
                for (let i = 1; i <= 7; i++) data.push({ date: `+${i}d`, actual: null as any, forecast: last * (1 + seededRandom(i) * 0.2) });
                return <MultiSeriesLine title={title('revenue_forecast')} data={data} series={[
                    { key: 'actual', color: CHART_COLORS.revenue, label: 'Actual' },
                    { key: 'forecast', color: CHART_COLORS.accent, label: 'Forecast' }
                ]} />;
            }
            case CHART_TYPES.EXPENSE_FORECAST: {
                const data = daily.map(d => ({ date: d.date, actual: d.expense, forecast: null as number | null }));
                const last = daily[daily.length - 1]?.expense || 0;
                for (let i = 1; i <= 7; i++) data.push({ date: `+${i}d`, actual: null as any, forecast: last * (1 + seededRandom(i + 11) * 0.2) });
                return <MultiSeriesLine title={title('expense_forecast')} data={data} series={[
                    { key: 'actual', color: CHART_COLORS.expense, label: 'Actual' },
                    { key: 'forecast', color: CHART_COLORS.warning, label: 'Forecast' }
                ]} />;
            }
            case CHART_TYPES.PROFIT_FORECAST: {
                const data = daily.map(d => ({ date: d.date, actual: d.net, forecast: null as number | null }));
                const last = daily[daily.length - 1]?.net || 0;
                for (let i = 1; i <= 7; i++) data.push({ date: `+${i}d`, actual: null as any, forecast: last * (0.9 + seededRandom(i + 23) * 0.4) });
                return <MultiSeriesLine title={title('profit_forecast')} data={data} series={[
                    { key: 'actual', color: CHART_COLORS.net, label: 'Actual' },
                    { key: 'forecast', color: CHART_COLORS.accent, label: 'Forecast' }
                ]} />;
            }
            case CHART_TYPES.BREAK_EVEN_PROJECTION:
                return <KpiCard label={title('break_even_projection')} value={`Day ${Math.floor(daily.length * 0.6) + 1}`} color="text-emerald-600" sub="Projected break-even" icon={<Target size={11} />} />;
            case CHART_TYPES.SEASONAL_INDEX: {
                const max = Math.max(...dowAggregate.map(d => d.revenue));
                return (
                    <div className="w-full h-full flex flex-col p-2">
                        <ChartTitle>{title('seasonal_index')}</ChartTitle>
                        <div className="grid grid-cols-7 gap-1 flex-1">
                            {dowAggregate.map(d => (
                                <div key={d.day} className="flex flex-col items-center justify-end">
                                    <div className="w-full rounded-t-md bg-emerald-500/40 transition-all" style={{ height: `${max > 0 ? (d.revenue / max) * 90 : 0}%` }} />
                                    <span className="text-[8px] font-bold text-neutral-400 mt-1 uppercase">{d.day}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            }
            case CHART_TYPES.WHAT_IF_SIMULATOR:
                return (
                    <div className="w-full h-full flex flex-col p-3">
                        <ChartTitle>{title('what_if_simulator')}</ChartTitle>
                        <div className="flex-1 min-h-0 flex flex-col justify-center gap-2">
                            {[
                                { label: 'Ad Spend', value: 60 },
                                { label: 'AOV', value: 40 },
                                { label: 'Conversion', value: 75 }
                            ].map(s => (
                                <div key={s.label}>
                                    <div className="flex justify-between text-[10px] text-neutral-500 font-medium mb-1">
                                        <span>{s.label}</span>
                                        <span className="font-bold text-neutral-700">+{s.value}%</span>
                                    </div>
                                    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-violet-500 rounded-full" style={{ width: `${s.value}%` }} />
                                    </div>
                                </div>
                            ))}
                            <div className="mt-2 text-center">
                                <div className="text-[10px] text-neutral-500">Projected Profit</div>
                                <div className="text-lg font-bold text-emerald-600">{formatMoney(totals.netIncome * 1.6)}</div>
                            </div>
                        </div>
                    </div>
                );

            // ── Campaign & Ad Changes ────────────────────────────────────
            case CHART_TYPES.ACTIVITY_TIMELINE:
                return <EventTimeline title={title('activity_timeline')} items={mockData.events.map(e => ({ date: format(parseISO(e.date), 'MMM dd'), name: e.name, impact: e.impact, type: e.type }))} />;
            case CHART_TYPES.REVENUE_WITH_ACTION_MARKERS: {
                const eventDates = new Set(mockData.events.map(e => e.date));
                const data = daily.map(d => ({ ...d, marker: eventDates.has(d.date) ? d.revenue : null }));
                return (
                    <div className="w-full h-full flex flex-col p-2">
                        <ChartTitle>{title('revenue_with_action_markers')}</ChartTitle>
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                                    <XAxis dataKey="date" hide />
                                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                                    <Line type="monotone" dataKey="revenue" stroke={CHART_COLORS.revenue} strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="marker" stroke={CHART_COLORS.warning} strokeWidth={0} dot={{ r: 5, fill: CHART_COLORS.warning }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );
            }
            case CHART_TYPES.BUDGET_CHANGE_IMPACT:
                return <BeforeAfter title={title('budget_change_impact')} label="ROAS change" before={3.2} after={4.1} valueFormatter={v => `${v.toFixed(1)}x`} />;
            case CHART_TYPES.BUDGET_INCREASE_EFFICIENCY:
                return <ScatterView title={title('budget_increase_efficiency')} xLabel="Budget Δ" yLabel="ROAS Δ" points={Array.from({ length: 18 }).map((_, i) => ({
                    x: -50 + seededRandom(i * 3) * 200,
                    y: -1 + seededRandom(i * 5) * 3,
                    z: 50 + seededRandom(i * 7) * 100
                }))} />;
            case CHART_TYPES.CAMPAIGN_TOGGLE_IMPACT:
                return <EventTimeline title={title('campaign_toggle_impact')} items={[
                    { date: 'Apr 18', name: 'Launched: Spring Promo', impact: 'high', type: 'Launch' },
                    { date: 'Apr 21', name: 'Paused: Lookalike v3', impact: 'medium', type: 'Pause' },
                    { date: 'Apr 24', name: 'Resumed: Retargeting', impact: 'low', type: 'Resume' },
                    { date: 'Apr 28', name: 'Launched: Creator Collab', impact: 'high', type: 'Launch' }
                ]} />;
            case CHART_TYPES.PAUSE_RESUME_COMPARISON:
                return <BeforeAfter title={title('pause_resume_comparison')} label="vs paused" before={1200} after={1850} />;
            case CHART_TYPES.CREATIVE_SWAP_PERFORMANCE:
                return <BeforeAfter title={title('creative_swap_performance')} label="CTR change" before={1.8} after={3.1} valueFormatter={v => `${v.toFixed(1)}%`} />;
            case CHART_TYPES.AUDIENCE_CHANGE_IMPACT:
                return <BeforeAfter title={title('audience_change_impact')} label="ROAS change" before={2.4} after={3.6} valueFormatter={v => `${v.toFixed(1)}x`} />;
            case CHART_TYPES.AD_APPROVAL_TRACKER:
                return <StatusList title={title('ad_approval_tracker')} items={[
                    { name: 'Spring carousel', status: 'ok', meta: 'Approved' },
                    { name: 'UGC reel #4', status: 'pending', meta: 'In review' },
                    { name: 'Discount banner', status: 'error', meta: 'Rejected' },
                    { name: 'Holiday teaser', status: 'warn', meta: 'Limited' },
                    { name: 'Brand video', status: 'ok', meta: 'Approved' }
                ]} />;
            case CHART_TYPES.CAMPAIGN_LIFECYCLE:
                return <StatusList title={title('campaign_lifecycle')} items={[
                    { name: 'Spring Promo', status: 'ok', meta: 'Active · 14d' },
                    { name: 'Lookalike v3', status: 'warn', meta: 'Paused · 3d' },
                    { name: 'Retargeting', status: 'ok', meta: 'Active · 42d' },
                    { name: 'Brand Search', status: 'ok', meta: 'Active · 90d' },
                    { name: 'Creator Collab', status: 'pending', meta: 'Scheduled' }
                ]} />;

            // ── Store & Product Changes ──────────────────────────────────
            case CHART_TYPES.PRODUCT_LAUNCH_PERFORMANCE: {
                const data = daily.map((d, i) => ({ date: d.date, revenue: d.revenue, launch: i === Math.floor(daily.length * 0.3) ? d.revenue : null }));
                return (
                    <div className="w-full h-full flex flex-col p-2">
                        <ChartTitle>{title('product_launch_performance')}</ChartTitle>
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={CHART_COLORS.grid} />
                                    <XAxis dataKey="date" hide />
                                    <YAxis tick={{ fontSize: 9, fill: CHART_COLORS.text }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                                    <Line type="monotone" dataKey="revenue" stroke={CHART_COLORS.revenue} strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="launch" stroke={CHART_COLORS.accent} strokeWidth={0} dot={{ r: 8, fill: CHART_COLORS.accent }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );
            }
            case CHART_TYPES.PRODUCT_REMOVAL_IMPACT:
                return <BeforeAfter title={title('product_removal_impact')} label="revenue change" before={4800} after={4200} />;
            case CHART_TYPES.PROMO_DISCOUNT_RULE_IMPACT:
                return <EventTimeline title={title('promo_discount_rule_impact')} items={[
                    { date: 'Apr 12', name: 'BOGO 50% on Bundle', impact: 'high', type: 'Promo' },
                    { date: 'Apr 19', name: 'Free shipping over $50', impact: 'medium', type: 'Rule' },
                    { date: 'Apr 22', name: 'First-time 15% off', impact: 'low', type: 'Promo' },
                    { date: 'Apr 27', name: 'Bundle discount removed', impact: 'medium', type: 'Rule' }
                ]} />;
            case CHART_TYPES.REFUND_SPIKE_ANALYSIS:
                return (
                    <div className="w-full h-full flex flex-col p-2 bg-red-50/30">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="p-1.5 bg-red-100 rounded-lg text-red-600"><AlertTriangle size={14} /></div>
                            <div>
                                <span className="block text-[10px] uppercase tracking-widest text-red-400 font-bold leading-none">{title('refund_spike_analysis')}</span>
                                <span className="text-[11px] font-bold text-red-700 mt-1">Spike on Apr 12</span>
                            </div>
                        </div>
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={daily.map(d => ({ date: d.date, refunds: d.refunds }))} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                    <Bar dataKey="refunds" fill={CHART_COLORS.expense} radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            // ── Operational Health ───────────────────────────────────────
            case CHART_TYPES.ORDER_FULFILLMENT_FUNNEL:
                return <Funnel title={title('order_fulfillment_funnel')} stages={[
                    { name: 'Placed', value: ordersTotal, color: CHART_COLORS.net },
                    { name: 'Paid', value: Math.round(ordersTotal * 0.95), color: CHART_COLORS.revenue },
                    { name: 'Fulfilled', value: Math.round(ordersTotal * 0.88), color: CHART_COLORS.accent },
                    { name: 'Delivered', value: Math.round(ordersTotal * 0.82), color: CHART_COLORS.info }
                ]} />;
            case CHART_TYPES.CANCELLATION_RATE_OVER_TIME: {
                const data = daily.map((d, i) => ({ date: d.date, rate: 1 + Math.sin(i / 4) + seededRandom(i) }));
                return <TrendLine title={title('cancellation_rate_over_time')} data={data} dataKey="rate" color={CHART_COLORS.expense} yFormatter={v => `${v.toFixed(1)}%`} />;
            }
            case CHART_TYPES.ACTIVITY_VOLUME_VS_REVENUE:
                return <MultiSeriesLine title={title('activity_volume_vs_revenue')} data={daily} series={[
                    { key: 'orders', color: CHART_COLORS.net, label: 'Orders' },
                    { key: 'revenue', color: CHART_COLORS.revenue, label: 'Revenue' }
                ]} />;
            case CHART_TYPES.BILLING_ISSUES_TIMELINE:
                return <StatusList title={title('billing_issues_timeline')} items={[
                    { name: 'Stripe declined', status: 'error', meta: 'Apr 22' },
                    { name: 'Payout delayed', status: 'warn', meta: 'Apr 19' },
                    { name: 'Card update needed', status: 'pending', meta: 'Apr 15' },
                    { name: 'Refund processed', status: 'ok', meta: 'Apr 11' }
                ]} />;
            case CHART_TYPES.ACTION_BREAKDOWN_BY_TYPE:
                return <Donut title={title('action_breakdown_by_type')} data={[
                    { name: 'Launch', value: 24, color: CHART_COLORS.revenue },
                    { name: 'Pause', value: 18, color: CHART_COLORS.warning },
                    { name: 'Budget Δ', value: 32, color: CHART_COLORS.accent },
                    { name: 'Resume', value: 12, color: CHART_COLORS.info },
                    { name: 'Other', value: 14, color: CHART_COLORS.text }
                ]} />;
            case CHART_TYPES.BEFORE_AFTER_ANY_ACTION:
                return <BeforeAfter title={title('before_after_any_action')} label="net change" before={2100} after={2750} />;

            default:
                return (
                    <div className="flex flex-col items-center justify-center h-full text-neutral-400 bg-neutral-50/50 group/chart-default">
                        <div className="p-3 bg-white rounded-2xl shadow-sm border border-neutral-100 mb-3 group-hover/chart-default:scale-110 transition-transform duration-300">
                            <LayoutIcon size={28} className="text-neutral-300" />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">{title(type)}</span>
                        <div className="flex items-center gap-1.5 mt-3 px-3 py-1 bg-white border border-neutral-200 rounded-full shadow-sm text-[9px] font-semibold text-neutral-400">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            {t('charts.preview_available')}
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="w-full h-full bg-white flex flex-col group/chart overflow-hidden">
            <div className="flex-1 min-h-0">
                {renderChartContent()}
            </div>
        </div>
    );
};
